import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import { Markdown } from 'tiptap-markdown';

/**
 * Shared TipTap extension set for the clinical rich-text editor (SHER-271 PR2).
 *
 * The stored artifact is **clean HTML**: the schema is constrained to exactly
 * the tags the screen/PDF renderers can render (see RENDERABLE_TAGS), so the
 * editor can never produce content a renderer would drop — that is the
 * prevention. (Markdown serialization is kept available but dormant; HTML is
 * the canonical format — plan decision 2026-06-08.)
 */

/**
 * The HTML tags the renderers (PDFSurveyRecordRenderer / renderSimpleHTML and
 * the on-screen viewer) can render. Single source of truth: the editor schema
 * is locked to this set and the golden test asserts the editor output stays
 * within it. Keep in sync with the renderer.
 */
export const RENDERABLE_TAGS = [
  'p', 'br',
  'strong', 'b', 'em', 'i', 'u',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'table', 'thead', 'tbody', 'tr', 'td', 'th', 'colgroup', 'col',
] as const;

// Underline isn't a Markdown concept; if the (dormant) Markdown serializer is
// ever used, emit it as inline <u> HTML.
const MarkdownUnderline = Underline.extend({
  addStorage() {
    return { markdown: { serialize: { open: '<u>', close: '</u>' }, parse: {} } };
  },
});

export const markdownEditorExtensions = [
  StarterKit.configure({
    // Lock the schema to the renderer allowlist: disable nodes/marks the
    // renderers don't handle, so they cannot be produced or survive a paste.
    blockquote: false,
    codeBlock: false,
    code: false,
    strike: false,
    horizontalRule: false,
  }),
  MarkdownUnderline,
  Table.configure({ resizable: false }),
  TableRow,
  TableHeader,
  TableCell,
  // Dormant: HTML is canonical. Kept so `format="markdown"` still works if needed.
  Markdown.configure({
    html: true,
    breaks: false,
    transformPastedText: true,
    transformCopiedText: false,
  }),
];
